import React, { useState, useEffect } from 'react';
import TimeSeriesChart from '@/components/shared/TimeSeriesChart';
import { Card } from '@/components/ui/Card';
import { format, subHours } from 'date-fns';
import { apiService } from '@/utils/api';
import { useAnalytics } from './AnalyticsContext';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { RootState } from '@/store';

interface TimeSeriesPoint {
  timestamp: string;
  value: number;
}

interface TimeSeriesData {
  metric: string;
  points: TimeSeriesPoint[];
}

const FlowRatePage: React.FC = () => {
  // Get device ID from context
  const { deviceId } = useAnalytics();
  const { mode } = useSelector((state: RootState) => state.theme);
  
  // Default settings
  const [endTimeState, setEndTimeState] = useState<Date>(new Date());
  const [startTimeState, setStartTimeState] = useState<Date>(subHours(new Date(), 24));
  
  const [interval, setInterval] = useState<string>('15m');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentFlowRate, setCurrentFlowRate] = useState<number>(0);
  const [averageFlowRate, setAverageFlowRate] = useState<number>(0);
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesData[]>([]);
  
  // Format as ISO strings with UTC timezone
  const formattedStartTime = startTimeState.toISOString();
  const formattedEndTime = endTimeState.toISOString();
  
  // Determine flow rate quality based on value
  const getFlowQuality = (flowRate: number): { label: string; colorClass: string } => {
    if (flowRate >= 15) {
      return { label: 'High', colorClass: 'text-blue-600 dark:text-blue-400 bg-blue-100 dark:bg-blue-500/20' };
    } else if (flowRate >= 5) {
      return { label: 'Normal', colorClass: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-500/20' };
    } else if (flowRate > 0) {
      return { label: 'Low', colorClass: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-500/20' };
    } else {
      return { label: 'No Flow', colorClass: 'text-red-600 dark:text-red-400 bg-red-100 dark:bg-red-500/20' };
    }
  };
  
  // Fetch flow rate data when device ID or time parameters change
  useEffect(() => {
    if (!deviceId) return;
    
    const fetchFlowData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = {
          device_id: deviceId,
          metrics: ['flow_rate'],
          start_time: formattedStartTime,
          end_time: formattedEndTime,
          interval: interval
        };
        
        const data = await apiService.post<TimeSeriesData[]>('/api/v1/sensor-data/time-series', params);
        
        // Reset states when data is empty or invalid
        if (!data || data.length === 0 || data[0].points.length === 0) {
          setCurrentFlowRate(0);
          setAverageFlowRate(0);
          setTimeSeriesData([]);
          return;
        }
        
        // Get latest flow rate as current value
        const latestPoint = data[0].points[data[0].points.length - 1];
        setCurrentFlowRate(latestPoint.value);
        
        // Calculate average flow rate
        const sum = data[0].points.reduce((acc: number, point: TimeSeriesPoint) => acc + point.value, 0);
        const avg = sum / data[0].points.length;
        setAverageFlowRate(parseFloat(avg.toFixed(1)));
        
        // Save time series data for chart
        setTimeSeriesData(data);
      } catch (err) {
        console.error('Error fetching flow rate data:', err);
        setError('Failed to fetch flow rate data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchFlowData();
  }, [deviceId, formattedStartTime, formattedEndTime, interval]);
  
  const handleIntervalChange = (newInterval: string) => {
    setInterval(newInterval);
  };
  
  const handleDateRangeChange = (newStartTime: string, newEndTime: string) => {
    // The received times are already in UTC format, so we need to convert them to local time
    const startDate = new Date(newStartTime);
    const endDate = new Date(newEndTime);
    
    // No need to adjust for timezone since Date constructor already handles UTC 'Z' suffix
    setStartTimeState(startDate);
    setEndTimeState(endDate);
  };
  
  const currentQuality = getFlowQuality(currentFlowRate);
  const averageQuality = getFlowQuality(averageFlowRate);
  
  return (
    <div className="page-container relative h-full flex flex-auto flex-col px-4 sm:px-6 md:px-8 py-4 sm:py-6 md:px-8">
      <div className="flex flex-col gap-4 h-full">
        <div className="lg:flex items-center justify-between mb-4 gap-3">
          <div className="mb-4 lg:mb-0">
            <h3 className={classNames(
              "text-lg font-semibold",
              {
                'text-gray-900': mode === 'light',
                'text-gray-200': mode === 'dark'
              }
            )}>Flow Rate</h3>
            <p className={classNames(
              "mt-1 text-sm",
              {
                'text-gray-500': mode === 'light',
                'text-gray-400': mode === 'dark'
              }
            )}>Monitor your device's water flow rate</p>
          </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 lg:gap-4">
          <div className="card card-border" role="presentation">
            <div className="card-body">
              <h6 className="font-semibold mb-4 text-sm">Current Flow Rate</h6>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold">
                    <span>{currentFlowRate} L/min</span>
                  </h3>
                  <p>Flow Rate</p>
                  {/* <p>Last updated: <span className="font-semibold">{format(new Date(), 'HH:mm:ss')}</span></p> */}
                </div>
                {/* {currentFlowRate !== 0 && (
                  <div className="relative group">
                    <div className={`tag gap-1 font-bold border-0 ${currentQuality.colorClass}`}>
                      <span>{currentQuality.label}</span>
                    </div>
                    <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                      <p>Flow rate interpretation:</p>
                      <p>Above 15 L/min: High</p>
                      <p>5-15 L/min: Normal</p>
                      <p>0-5 L/min: Low</p>
                      <p>0 L/min: No Flow</p>
                    </div>
                  </div>
                )} */}
              </div>
            </div>
          </div>
          
          <div className="card card-border" role="presentation">
            <div className="card-body">
              <h6 className="font-semibold mb-4 text-sm">Average Flow Rate</h6>
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-bold">
                    <span>{averageFlowRate} L/min</span>
                  </h3>
                  <p>Flow Rate</p>
                  <p>Last 24 hours</p>
                </div>
                {averageFlowRate !== 0 && (
                  <div className="relative group">
                    <div className={`tag gap-1 font-bold border-0 ${averageQuality.colorClass}`}>
                      <span>{averageQuality.label}</span>
                    </div>
                    <div className="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-opacity z-10">
                      <p>Flow rate interpretation:</p>
                      <p>Above 15 L/min: High</p>
                      <p>5-15 L/min: Normal</p>
                      <p>0-5 L/min: Low</p>
                      <p>0 L/min: No Flow</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="grid grid-cols-1 gap-4">
          {timeSeriesData.length > 0 ? (
            <div className="card card-border">
              <div className="card-body">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h4>Flow Rate Over Time</h4>
                    <p>Monitor water flow rate fluctuations</p>
                  </div>
                </div>
                
                {loading && (
                  <div className="flex justify-center items-center h-64">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                  </div>
                )}
                
                {error && (
                  <div className="alert alert-danger">
                    {error}
                  </div>
                )}
                
                {!loading && !error && (
                  <TimeSeriesChart
                    deviceId={deviceId}
                    startTime={formattedStartTime}
                    endTime={formattedEndTime}
                    interval={interval}
                    height={500}
                    data={timeSeriesData}
                    showTitle={false}
                    onIntervalChange={handleIntervalChange}
                    onDateRangeChange={handleDateRangeChange}
                    metricKey="flow_rate"
                    yAxisTitle="Flow Rate (L/min)"
                    tooltipValueSuffix=" L/min"
                    seriesName="Flow Rate (L/min)"
                    chartColor="#3b82f6"
                    title="Flow Rate"
                    subtitle="Monitor water flow rate over time"
                  />
                )}
              </div>
            </div>
          ) : (
            <div className="flex justify-center items-center h-64">
              <p>No data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FlowRatePage; 